import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Control, Controller, FieldValues, UseFormSetValue, useWatch } from 'react-hook-form'
import { Select } from '@/components/atoms/select'
import { Select as AntSelect } from 'antd'
import {
  SalesDepartmentMasterType,
  SalesSubjectTaxType,
  SalesSubjectMasterType,
  SalesSubSubjectMasterType,
} from '@/models/sales-manager/sales'
import { fetchSalesDepartment, fetchSalesSubjectMaster } from '@/apis/aipass'
import { RequestDetailType, SpaceDetailType } from '@/models/guest-app/asset'
import { SubjectCascaeder } from '@/components/molecules/subject-cascader'
import uuid from 'react-uuid'

interface FormValue extends FieldValues {
  salesDepartmentId: string | undefined
  salesSubjectMasterId: string | undefined
}
type Props<T extends FormValue> = {
  control: T extends FormValue ? Control<T, any> : never
  detail: SpaceDetailType | RequestDetailType | SalesSubjectTaxType | null
  setValue: T extends FormValue ? UseFormSetValue<T> : never
  disableLabel?: boolean
  required?: boolean
  errorMessage?: string
}

export const InputSalesSubject = <T extends FormValue>({ control, setValue, detail, ...props }: Props<T>) => {
  const { t } = useTranslation()
  const watchSalesDepartmentId = useWatch({ control, name: 'salesDepartmentId' })
  const [salesDepartmentMasters, setSalesDepartmentMasters] = useState<SalesDepartmentMasterType[]>([])
  const [salesSubjectMasters, setSalesSubjectMasters] = useState<SalesSubjectMasterType[]>([])
  const [isFetchedMaster, setIsFetchedMaster] = useState(false)

  const findSameCodeMaster = (currentValue: {
    subjectName: string
    subjectCode: string
    subSubjectName: string | undefined
    subSubjectCode: string | undefined
  }): SalesSubjectMasterType | SalesSubSubjectMasterType | undefined => {
    for (const master of salesSubjectMasters) {
      const isSameMaster = master.subjectName === currentValue.subjectName && master.subjectCode === currentValue.subjectCode
      if (!isSameMaster) {
        continue
      }
      const isSelectedSubSubject = !!currentValue.subSubjectCode && currentValue.subSubjectCode !== '-'
      if (!isSelectedSubSubject) {
        return master
      }

      for (const subMaster of master.subSubjects) {
        const isSameSubMaster =
          subMaster.subSubjectName === currentValue.subSubjectName && subMaster.subSubjectCode === currentValue.subSubjectCode
        if (isSameSubMaster) {
          return subMaster
        }
      }
    }
  }

  const initializeComponent = async (opt?: { noIndicator: boolean }) => {
    await Promise.all([
      fetchSalesDepartment().then(res => {
        const departments = (res || []).filter(r => r.relatedSubjectMasterCount)
        const existsMasterOfDetail = departments.find(
          master =>
            master.departmentCode === detail?.salesSubject?.salesDepartmentCode &&
            master.departmentName === detail?.salesSubject?.salesDepartmentName,
        )
        const isNeedReplaceId = existsMasterOfDetail && existsMasterOfDetail?.id !== detail?.salesSubject?.salesDepartmentId
        if (isNeedReplaceId) {
          setValue('salesDepartmentId', existsMasterOfDetail.id)
        }

        if (detail?.salesSubject && !existsMasterOfDetail) {
          departments.push({
            id: detail.salesSubject.salesDepartmentId,
            departmentCode: detail.salesSubject.salesDepartmentCode,
            departmentName: detail.salesSubject.salesDepartmentName,
            relatedSubjectMasterCount: 1,
          })
        }
        setSalesDepartmentMasters(departments)
      }),
      fetchSalesSubjectMaster().then(salesSubjects => {
        if (!detail?.salesSubject) {
          setSalesSubjectMasters(salesSubjects)
          return
        }

        const sameMaster = findSameCodeMaster({
          subjectName: detail.salesSubject.salesSubjectMasterName,
          subjectCode: detail.salesSubject.salesSubjectMasterCode,
          subSubjectName: detail.salesSubject.salesSubSubjectName,
          subSubjectCode: detail.salesSubject.salesSubSubjectCode,
        })

        if (!sameMaster) {
          const isSelectedSubSubject = detail.salesSubject.salesSubSubjectCode && detail.salesSubject.salesSubSubjectCode !== '-'
          salesSubjects.push({
            id: isSelectedSubSubject ? uuid() : detail.salesSubject.salesSubjectMasterId,
            subjectCode: detail.salesSubject.salesSubjectMasterCode,
            subjectName: detail.salesSubject.salesSubjectMasterName,
            departmentId: detail.salesSubject.salesDepartmentId,
            departmentCode: detail.salesSubject.salesDepartmentCode,
            departmentName: detail.salesSubject.salesDepartmentName,
            category: undefined,
            separated: undefined,
            price: 0,
            tax: 0,
            planCodes: [],
            displayOrder: 0,
            isSubSubjectOnly: false,
            subSubjects: !isSelectedSubSubject
              ? []
              : [
                  {
                    id: uuid(),
                    defaultMasterId: detail.salesSubject.salesSubjectMasterId,
                    salesSubjectMasterId: detail.salesSubject.salesSubjectMasterId,
                    subSubjectCode: detail.salesSubject.salesSubSubjectCode,
                    subSubjectName: detail.salesSubject.salesSubSubjectName,
                    price: 0,
                    displayOrder: 0,
                  },
                ],
          } as SalesSubjectMasterType)
          setSalesSubjectMasters(salesSubjects)
          return
        }

        if ('subSubjectCode' in sameMaster) {
          if (sameMaster.salesSubjectMasterId !== detail.salesSubject.salesSubjectMasterId) {
            setValue('salesSubjectMasterId', sameMaster.salesSubjectMasterId)
          }
        } else {
          if (sameMaster.id !== detail.salesSubject.salesSubjectMasterId) {
            setValue('salesSubjectMasterId', sameMaster.id)
          }
        }
      }),
    ])
    setIsFetchedMaster(true)
  }

  useEffect(() => {
    initializeComponent()
  }, [])

  return (
    <div css={inputAreaStyle}>
      <div style={{ paddingRight: 8, width: '50%' }}>
        {!props.disableLabel && (
          <div css={inputTitleTextStyle}>
            {t('Department ID')}/{t('Department name')}
          </div>
        )}
        <Controller
          name="salesDepartmentId"
          control={control}
          rules={{ required: props.required ? t('Please select subjects') : false }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              value={isFetchedMaster ? value : props.required ? undefined : ''}
              onChange={e => {
                setValue('salesSubjectMasterId', '')
                onChange(e)
              }}
              customStyle={css({ paddingBottom: 24 })}
              error={error?.message ? props.errorMessage || error.message : undefined}
              placeholder={t('Please select')}
            >
              {!props.required && <AntSelect.Option value="">{t('Dont choose')}</AntSelect.Option>}
              {salesDepartmentMasters.map(salesDepartment => (
                <AntSelect.Option key={salesDepartment.id} value={salesDepartment.id}>
                  {salesDepartment.departmentCode}/{salesDepartment.departmentName}
                </AntSelect.Option>
              ))}
            </Select>
          )}
        />
      </div>
      {!!watchSalesDepartmentId && (
        <div style={{ paddingLeft: 8, width: '50%' }}>
          {!props.disableLabel && (
            <div css={inputTitleTextStyle}>
              {t('Course ID')}/{t('Subject name')}
              <div css={requireLabelTextStyle}>※</div>
            </div>
          )}
          <Controller
            name="salesSubjectMasterId"
            control={control}
            rules={{ required: t('Please select subjects') }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <SubjectCascaeder
                salesSubjectMasters={salesSubjectMasters}
                value={value}
                filterSalesDepartmentId={watchSalesDepartmentId}
                onChange={onChange}
                error={error?.message}
                popupPlacement="bottomRight"
              />
            )}
          />
        </div>
      )}
    </div>
  )
}

const inputTitleTextStyle = css({
  display: 'flex',
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: '0.6px',
  color: '#676767',
  paddingBottom: 12,
})

const requireLabelTextStyle = css({
  fontSize: 8,
  fontWeight: 'bold',
  letterSpacing: '0.4px',
  color: '#676767',
  paddingLeft: 8,
})

const inputAreaStyle = css({
  display: 'flex',
})
